import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { map, Observable, startWith, tap } from 'rxjs';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { OgDataService } from '../../../core/seo/og-data.service';
import { NumeronCollectionModel } from '../models/numeron.model';
import { OrderedMasonryDirective } from '../../../shared/directives/ordered-masonry.directive';
import { OrderedMasonryItemDirective } from '../../../shared/directives/ordered-masonry-item.directive';
import { NumeronCardComponent } from '../../../shared/ui/numeron-card/numeron-card.component';
import { CollectionListCardComponent } from '../../../shared/ui/collection-list-card/collection-list-card.component';

interface CollectionPageViewModel {
  numeronCollection: NumeronCollectionModel | null;
  isLoading: boolean;
  hasError: boolean;
}

@Component({
  selector: 'app-collection-page',
  imports: [
    AsyncPipe,
    OrderedMasonryDirective,
    OrderedMasonryItemDirective,
    NumeronCardComponent,
    CollectionListCardComponent,
  ],
  templateUrl: './collection-page.component.html',
})
export class CollectionPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly ogDataService = inject(OgDataService);

  protected readonly vm$: Observable<CollectionPageViewModel> = this.route.data.pipe(
    map((data) => (data['numeronCollection'] as NumeronCollectionModel | null) ?? null),
    tap((numeronCollection) => this.setOgData(numeronCollection)),
    map((numeronCollection) => ({
      numeronCollection,
      isLoading: false,
      hasError: numeronCollection === null,
    })),
    startWith({
      numeronCollection: null,
      isLoading: true,
      hasError: false,
    }),
  );

  protected isStandardMode(type?: string): boolean {
    return type === 'Standard';
  }

  protected isListMode(type?: string): boolean {
    return type === 'List';
  }

  private setOgData(numeronCollection: NumeronCollectionModel | null): void {
    if (!numeronCollection) {
      return;
    }

    const ogTitle = numeronCollection.title;
    const ogUrl = `https://numerical.co.in/numerons/collection/${numeronCollection._id}`;
    const contentGroup =
      numeronCollection.topic?.topic?.trim() ??
      numeronCollection.numerons[0]?.topic?.topic?.trim() ??
      undefined;

    numeronCollection.ogdata = {
      title: ogTitle,
      url: ogUrl,
    };

    this.ogDataService.setOgData(ogTitle, ogUrl);
    this.ga4AnalyticsService.trackPageView({
      contentGroup,
      contentId: numeronCollection._id,
      contentType: 'Collection',
    });
  }
}
